// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import filesaver from 'file-saver';
import truncate from 'lodash4/truncate';
import {scaled_number, fmt_num as fmt_num_lib} from '/util/conv.js';
import csv from '/util/csv.js';
import {qs_parse, qs_parse_url, parse as parse_url} from '/util/url.js';
import date, {ms} from '/util/date.js';
import {Clipboard} from '/www/util/pub/react.js';
import etask from '/util/etask.js';
import {ERROR_ALERT_DURATION, SUCCESS_ALERT_DURATION,
    RATE_MAX_DIGITS} from '@pub/shared/constants.js';
import {ROUTES, CACHE_LIFETIME, CP_URL,
    URL_PREFIX} from '@pub/constants.js';
import {spawn} from '@pub/comp/notif_system';
import {round_money, fmt$, find_last,
    stringify_url_state} from '@pub/shared/util.js';

const RETRY_COUNT = 2;
const RETRY_INTERVAL = 3*ms.SEC;

export const parse_qs = ()=>qs_parse(document.location.search.slice(1));

export function parse_url_state(){
    const {state} = parse_qs();
    if (!state)
        return {};
    try {
        const parsed = JSON.parse(state);
        return parsed || {};
    } catch(e){
        return {};
    }
}

export function get_path(path=''){
    const q_index = path.indexOf('?');
    let searchless_path = path;
    if (q_index>=0)
        searchless_path = path.slice(0, q_index);
    return searchless_path + window.location.search;
}

export function fmt_num(val, unit){
    return fmt_num_lib(val, unit);
}

export const fmt_prc = (v, t)=>{
    if (!t && !v)
        return '0%';
    if (!t)
        return 'Inf';
    const res = v/t*100;
    return res<1 ? '<1%' : res.toFixed(1)+'%';
};

export const fmt_rate = amount=>fmt$(amount, '$', RATE_MAX_DIGITS);

export function bytes_to_readable(bw, sign='B', base=1024){
    return (scaled_number(bw, {base})||0)+sign;
}
export function bytes_to_gb(bw, sign='B', base=1024){
    return scaled_number(bw, {scale: 'G', units: false});
}
export function shorten_num(num){
    return scaled_number(num, {base: 1000})||0;
}

export function export_csv(list, header, output_name='download'){
    if (list[0]&&list[0].length!=header.length)
        throw new Error('Header length doesn\'t match columns length');
    const output = [header, ...list];
    const filename = `${output_name}.csv`;
    filesaver(csv.to_blob(output), filename);
}

export function get_qs_partner(){
    const {partner} = qs_parse_url(window.location.search);
    return partner;
}

export function is_same_day(date_1, date_2){
    const val_1 = date.align(date_1, 'DAY');
    const val_2 = date.align(date_2, 'DAY');
    return val_1.getTime()==val_2.getTime();
}

export function scroll_to_id(){
    const parsed = parse_url(document.location.href);
    if (!parsed.hash)
        return false;
    const id = parsed.hash.slice(1);
    const target = document.getElementById(id);
    if (!target)
        return false;
    try {
        target.scrollIntoView(true);
        const target_cn = 'sdk-global-target';
        target.classList.add(target_cn);
        setTimeout(()=>target?.classList.remove(target_cn), 3*ms.SEC);
        return true;
    } catch(e){
        return false;
    }
}

export const to_md = (d, opts)=>date.strftime('%b %d', d, opts);

export const to_ym = (d, opts)=>date.strftime('%Y-%m', d, opts);

export const to_ymd = (d, opts)=>date.strftime('%Y-%m-%d', d, opts);

export const to_ymdhm = (d, opts)=>
    date.strftime('%Y-%m-%d %H:%M UTC', d, opts);

export const to_ymd_wd = (d, opts)=>date.strftime('%Y-%m-%d (%A)', d, opts);

export const notify_error = (content, conf={duration: ERROR_ALERT_DURATION})=>
    spawn.error(content, conf);

export const notify_success =
    (content, conf={duration: SUCCESS_ALERT_DURATION})=>
        spawn.success(content, conf);

export const get_status = rec=>{
    const [last_status] = (rec.statuses || []).slice(-1);
    return last_status?.status;
};

export const with_cache = (fn, cache_lifetime=CACHE_LIFETIME)=>{
    const cache = new Map();
    const cached_fn = params=>etask(function*(){
        const key = JSON.stringify(params);
        if (cache.has(key))
        {
            const cache_record = cache.get(key);
            if (date()-cache_record.ts<=cache_lifetime)
                return cache_record.value;
            cache.delete(key);
        }

        const res = yield fn(params);
        cache.set(key, {ts: date(), value: res});
        return res;
    });
    cached_fn.flush_cache = ()=>cache.clear();
    return cached_fn;
};

export const with_retry = (fn, count=RETRY_COUNT, interval_ms=RETRY_INTERVAL)=>
(...args)=>etask(function*(){
    let retry = 0;
    let err = null;
    while (retry<count)
    {
        retry++;
        try {
            const res = yield fn(...args);
            if (res.error)
                throw new Error(res.error);
            return res;
        } catch(e){
            err = e;
        }
        yield etask.sleep(interval_ms);
    }
    throw new Error(err);
});

export const copy_to_cliboard = (content, opts={})=>{
    const {skip_notify=false} = opts;
    Clipboard.copy(content);
    if (!skip_notify)
        notify_success('Copied');
};

export const get_app_link = (uuid, ...sub_path)=>{
    let path = `${ROUTES.APPS}/${uuid}`;
    for (const sp of sub_path??[])
        path+=`/${sp}`;
    return get_path(path);
};

export const get_bundle_link = (uuid, ...sub_path)=>{
    let path = `${ROUTES.BUNDLES}/${uuid}`;
    for (const sp of sub_path??[])
        path+=`/${sp}`;
    return get_path(path);
};

export const app2link = app=>{
    const b_link = app.is_bundle
        ? get_bundle_link(app.uuid)
        : get_app_link(app.uuid);
    return get_path(b_link);
};

export const get_app_review_link = (uuid, ...sub_path)=>{
    let path = `${ROUTES.REVIEWS}/${uuid}`;
    for (const sp of sub_path??[])
        path+=`/${sp}`;
    return get_path(path);
};

export const get_partner_link = (uuid, ...sub_path)=>{
    let path = `${ROUTES.PARTNERS}/${uuid}`;
    for (const sp of sub_path??[])
        path+=`/${sp}`;
    return `${path}?partner=${uuid}`;
};

export const get_user_link = (uuid, ...sub_path)=>{
    let path = `${ROUTES.USERS}/${uuid}`;
    for (const sp of sub_path??[])
        path+=`/${sp}`;
    return path;
};

export const get_checklist_link = (os, version='latest')=>{
    const path = `${ROUTES.CHECKLISTS}/${os}/${version}`;
    return get_path(path);
};

export const exts_to_accept = exts=>exts.map(e=>`.${e}`).join(', ');

export const wrap_url_prefix = url=>URL_PREFIX+url;
export const wrap_cp_url = url=>CP_URL+url;

export const get_tier_logo_name = tier=>`badge_${tier}`;

export const nbsp = (repeat=1)=>'\u00A0'.repeat(repeat);

export const build_report_params = (from, to, partner, is_root)=>{
    const params = {};
    params.from = date.strftime('%F', from);
    params.to = date.strftime('%F', to);
    const partner_uuid = partner?.uuid||'all';
    const all_apps = partner_uuid=='all' && is_root;
    if (all_apps)
        params.all_apps = true;
    else
        params.partner_uuid = partner_uuid;
    return params;
};

export const build_stats_params = (opts={})=>{
    const {app_uuid, partner_uuid, with_money, from, to} = opts;
    const params = {};
    if (partner_uuid!='all')
        params.partner_uuid = partner_uuid;
    if (app_uuid=='all')
        params.all_apps = true;
    else
        params.app_uuid = app_uuid;
    if (with_money)
        params.with_money = true;
    if (from)
        params.from = to_ymd(from);
    if (to)
        params.to = to_ymd(to);
    return params;
};

const DEFAULT_MONTHS_SHOW = 5;

export const get_months_row = (opts={})=>{
    const {month_num=DEFAULT_MONTHS_SHOW, skip_first_day=false, page=1,
        skip=0} = opts;
    const records = [];
    let d = date();
    if (skip || skip_first_day&&d.getDate()==1)
        d = date.add(d, {month: -skip || -1});
    for (let i=0; i<month_num*page; i++)
    {
        records.push({
            value: date.strftime('%Y_%m', d),
            label: date.strftime('%B %Y', d),
        });
        d = date.add(d, {month: -1});
    }
    return records;
};

export const get_metric_val_fmt = kind=>({
    earnings: {round: round_money, fmt: fmt$},
    bw: {round: Math.round, fmt: bytes_to_readable},
    ips: {round: Math.round, fmt: fmt_num},
    devices: {round: Math.round, fmt: fmt_num},
    devices_qualified: {round: Math.round, fmt: fmt_num},
    installs: {round: Math.round, fmt: fmt_num},
    optins: {round: Math.round, fmt: fmt_num},
}[kind]||{});

export const get_embedded_data = (field, default_value={})=>{
    return window.embedded_data?.[field] || default_value;
};

export const call_api = (fn, opts={})=>etask(function*(){
    if (!opts.do_throw)
    {
        this.on('uncaught', e=>{
            console.error(e);
            notify_error('Unknown error');
        });
    }
    const res = yield fn();
    if (res?.error)
        return void notify_error(res.error);
    return res;
});

export const ls_set = (key, value)=>{
    try {
        const to_save = JSON.stringify(value);
        window.localStorage.setItem(key, to_save);
        return true;
    } catch(e){
        console.error(e);
        return false;
    }
};

export const ls_get = (key, def)=>{
    try {
        const res = window.localStorage.getItem(key);
        return res===null ? def : JSON.parse(res);
    } catch(e){
        return def;
    }
};

export const limit_txt = (txt, limit=42)=>truncate(txt, {length: limit});

export const is_current_user_of_partner = partner_uuid=>{
    const partner_meta = get_embedded_data('partner');
    return partner_meta?.uuid==partner_uuid;
};

export {fmt$, find_last, stringify_url_state};
